import { normalizeGapFrames } from '../lib/captureGaps';

const TICK_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

export const timeToX = (time, domainSeconds, width) => {
  if (!(domainSeconds > 0)) return 0;
  const t = Number.isFinite(time) ? time : 0;
  const x = (t / domainSeconds) * width;
  if (x < 0) return 0;
  if (x > width) return width;
  return x;
};

export const xToTime = (x, domainSeconds, width) => {
  if (!(width > 0)) return 0;
  const d = domainSeconds > 0 ? domainSeconds : 0;
  const px = Number.isFinite(x) ? x : 0;
  const t = (px / width) * d;
  if (t < 0) return 0;
  if (t > d) return d;
  return t;
};

export const frameAtTime = (time, fps, frameCount) => {
  if (!(frameCount > 0)) return -1;
  const t = Number.isFinite(time) ? time : 0;
  const f = Math.floor(t * fps);
  if (f < 0) return 0;
  if (f > frameCount - 1) return frameCount - 1;
  return f;
};

// Segments are sorted, non-overlapping. `end` is exclusive.
export const segmentAtFrame = (frame, segments) => {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  let lo = 0;
  let hi = segments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const seg = segments[mid];
    if (frame < seg.start) {
      hi = mid - 1;
    } else if (frame >= seg.end) {
      lo = mid + 1;
    } else {
      return seg;
    }
  }
  return null;
};

// Min-max envelope: guarantees a non-empty bucket per column so narrow spikes
// never disappear when the source has many more frames than pixels.
export const buildEnvelope = (values, columns) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  if (!(columns > 0)) return [];
  const n = values.length;
  const out = new Array(columns);
  for (let i = 0; i < columns; i++) {
    let start = Math.floor((i * n) / columns);
    let end = Math.floor(((i + 1) * n) / columns);
    if (end <= start) {
      const idx = Math.min(n - 1, Math.max(0, start));
      const v = values[idx];
      out[i] = { min: v, max: v };
      continue;
    }
    let min = values[start];
    let max = values[start];
    for (let j = start + 1; j < end; j++) {
      const v = values[j];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    out[i] = { min, max };
  }
  return out;
};

// Capture-gap marker positions, in canvas pixels.
//
// A capture gap is a stretch where the camera was OFF (overnight, a weekend).
// `video.captureGapFrames` holds the FIRST frame AFTER each one, so gap frame g
// sits between motion pairs g-1 and g — exactly the pair the analysis worker
// zeroes out (`mask_capture_gap_pairs`, processing/motion_analysis.py). The
// marker is drawn on that boundary — which is where the render cuts segments
// too, but only when the capture-gap transitions setting is on.
//
// Index coercion (integers only, deduplicated, ascending) is shared with the
// render-side predictor via `normalizeGapFrames`; this adds only the marker's
// own 1..pairCount bound and the pixel mapping. Returns ascending x positions.
export const captureGapMarkerXs = (gapFrames, pairCount, width) => {
  if (!(pairCount > 0) || !(width > 0)) return [];
  return normalizeGapFrames(gapFrames)
    .filter((g) => g >= 1 && g <= pairCount)
    .map((g) => (g / pairCount) * width);
};

export const niceTimeTicks = (domainSeconds, maxTicks) => {
  if (!(domainSeconds > 0)) return [];
  const cap = Number.isFinite(maxTicks) && maxTicks > 0 ? Math.floor(maxTicks) : 1;
  let step = TICK_STEPS[TICK_STEPS.length - 1];
  for (const s of TICK_STEPS) {
    const count = Math.floor(domainSeconds / s) + 1;
    if (count <= cap) {
      step = s;
      break;
    }
  }
  // Domains past the largest predefined step (1h): synthesize a 2/5/10 "nice"
  // step so the tick count always respects the cap and the loop stays bounded.
  if (Math.floor(domainSeconds / step) + 1 > cap) {
    if (cap <= 1) return [0];
    const rawStep = domainSeconds / (cap - 1);
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const normalized = rawStep / magnitude;
    let nice = 10;
    if (normalized <= 2) nice = 2;
    else if (normalized <= 5) nice = 5;
    step = nice * magnitude;
  }
  const ticks = [];
  for (let t = 0; t <= domainSeconds; t += step) {
    ticks.push(t);
  }
  return ticks;
};

export const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s < 10 ? `0${s}` : `${s}`;
  if (h > 0) {
    const mm = m < 10 ? `0${m}` : `${m}`;
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
};
